import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  uploadFile,
  fetchVendorGroups,
  renameVendorGroup,
  generateVendorSpec,
  testVendorSpecUpload,
  promoteVendorSpec,
} from "./api";
import TestResultView from "./TestResultView";
import Modal from "./Modal";
import PdfPreviewModal from "./PdfPreviewModal";

const preStyle = {
  background: "#f8f9fa",
  padding: "8px",
  borderRadius: "4px",
  maxHeight: "260px",
  overflow: "auto",
  fontSize: "12px",
  marginTop: "8px",
  marginBottom: 0,
};

const STATUS_BADGE = {
  draft: "secondary",
  testing: "info",
  active: "success",
  escalated: "danger",
  retired: "dark",
};

const VERDICT_BADGE = {
  promote_candidate: "success",
  needs_review: "warning",
  reject: "danger",
};

const UnnamedLayoutRow = ({ layout, onGrouped, onPreview }) => {
  const [name, setName] = useState(layout.suggested_vendor_name || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const confirm = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await renameVendorGroup(layout.id, name.trim());
      onGrouped();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <tr>
      <td>{layout.filename}</td>
      <td>{layout.occurrences_count}</td>
      <td>
        <div className="input-group input-group-sm">
          <input
            type="text"
            className="form-control"
            placeholder="Vendor name..."
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <button type="button" className="btn btn-outline-primary" onClick={confirm} disabled={saving || !name.trim()}>
            {saving ? "Saving..." : "Group"}
          </button>
        </div>
        {error && <div className="text-danger small mt-1">{error}</div>}
      </td>
      <td>
        {layout.original_file_url && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary py-0 px-2"
            style={{ fontSize: "11px" }}
            onClick={() => onPreview({ url: layout.original_file_url, title: layout.filename })}
          >
            Preview
          </button>
        )}
      </td>
    </tr>
  );
};

const VendorGroupCard = ({ group, onChanged, onPreview }) => {
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [testFile, setTestFile] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [promoting, setPromoting] = useState(false);

  const spec = group.spec;

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      await generateVendorSpec(group.vendor_name);
      // Generation runs async (real OpenAI call) -- poll once after a beat
      // so the status/score has a chance to land, then let the operator
      // refresh manually via the panel's Refresh button for the final state.
      setTimeout(onChanged, 4000);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleTest = async () => {
    if (!testFile || !spec) return;
    setTesting(true);
    setTestResult(null);
    try {
      const uploaded = await uploadFile(testFile);
      const result = await testVendorSpecUpload(spec.vendor_key, uploaded.file_token);
      setTestResult(result);
    } catch (e) {
      setTestResult({ error: e.message });
    } finally {
      setTesting(false);
    }
  };

  const handlePromote = async () => {
    setPromoting(true);
    setError(null);
    try {
      await promoteVendorSpec(spec.vendor_key);
      onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setPromoting(false);
    }
  };

  const verdict = spec && spec.score && spec.score.verdict;
  const overallScore = spec && spec.score && spec.score.overall_score;

  return (
    <div className="card mb-2">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h6 className="card-title mb-0">{group.vendor_name}</h6>
            <div className="small text-muted">{group.layout_count} flagged sample(s)</div>
          </div>
          <div className="text-end">
            {spec && <span className={`badge bg-${STATUS_BADGE[spec.status] || "secondary"} me-1`}>{spec.status}</span>}
            {verdict && <span className={`badge bg-${VERDICT_BADGE[verdict] || "secondary"}`}>{verdict}</span>}
          </div>
        </div>

        {overallScore !== undefined && overallScore !== null && (
          <div className="small mt-1">
            Overall score: <strong>{overallScore}</strong>
          </div>
        )}

        <div className="d-flex align-items-center mt-2" style={{ gap: "8px", flexWrap: "wrap" }}>
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={handleGenerate} disabled={generating}>
            {generating ? "Enqueuing..." : spec ? "Regenerate spec" : "Generate spec"}
          </button>

          {spec && (
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setShowDetails((s) => !s)}>
              {showDetails ? "Hide details" : "Show details"}
            </button>
          )}

          {spec && spec.sample_file_url && (
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary"
              onClick={() => onPreview({ url: spec.sample_file_url, title: `${group.vendor_name} -- sample invoice` })}
            >
              Preview sample
            </button>
          )}

          {spec && spec.status !== "active" && spec.score && verdict !== "reject" && (
            <button type="button" className="btn btn-sm btn-success" onClick={handlePromote} disabled={promoting}>
              {promoting ? "Promoting..." : "Promote to production"}
            </button>
          )}
        </div>

        {error && <div className="alert alert-danger py-1 px-2 mt-2 mb-0 small">{error}</div>}

        {showDetails && spec && (
          <>
            <pre style={preStyle}>{JSON.stringify(spec.score, null, 2)}</pre>
            <pre style={preStyle}>{JSON.stringify(spec.spec, null, 2)}</pre>
          </>
        )}

        {spec && (
          <div className="mt-2 pt-2 border-top">
            <div className="small text-muted mb-1">Test this spec against another PDF:</div>
            <div className="input-group input-group-sm">
              <input
                type="file"
                accept=".pdf"
                className="form-control"
                onChange={(e) => setTestFile(e.target.files[0] || null)}
              />
              <button type="button" className="btn btn-outline-primary" onClick={handleTest} disabled={!testFile || testing}>
                {testing ? "Testing..." : "Test"}
              </button>
            </div>
            {testResult && <TestResultView result={testResult} />}
          </div>
        )}
      </div>
    </div>
  );
};

const VendorParserBuilderPanel = ({ refreshSignal }) => {
  const [groups, setGroups] = useState([]);
  const [unnamedLayouts, setUnnamedLayouts] = useState([]);
  const [productionSpecs, setProductionSpecs] = useState([]);
  const [loading, setLoading] = useState(false);
  // Nothing is fetched on page load -- only once the operator clicks "Load list".
  const [loaded, setLoaded] = useState(false);
  const loadedRef = useRef(false);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [preview, setPreview] = useState(null); // { url, title } | null

  const refresh = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchVendorGroups()
      .then((data) => {
        setGroups(data.groups || []);
        setUnnamedLayouts(data.unnamed_layouts || []);
        setProductionSpecs(data.production_specs || []);
        loadedRef.current = true;
        setLoaded(true);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // A step elsewhere (e.g. "Mark: no parser available") can change this list,
  // but only refresh it if the operator has already loaded it.
  useEffect(() => {
    if (loadedRef.current) refresh();
  }, [refresh, refreshSignal]);

  const body = (
    <>
      {error && <div className="alert alert-danger py-1 px-2 small">{error}</div>}

      {unnamedLayouts.length > 0 && (
        <>
          <h6 className="small text-uppercase text-muted mt-3">Needs a vendor name</h6>
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Filename</th>
                <th>Seen</th>
                <th>Vendor name</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {unnamedLayouts.map((layout) => (
                <UnnamedLayoutRow key={layout.id} layout={layout} onGrouped={refresh} onPreview={setPreview} />
              ))}
            </tbody>
          </table>
        </>
      )}

      <h6 className="small text-uppercase text-muted mt-3">Vendor groups</h6>
      {groups.length > 0 ? (
        groups.map((group) => <VendorGroupCard key={group.vendor_name} group={group} onChanged={refresh} onPreview={setPreview} />)
      ) : (
        <div className="text-muted small">No vendor groups yet.</div>
      )}

      <h6 className="small text-uppercase text-muted mt-3">Production parsers</h6>
      <p className="small text-muted mb-2" style={{ marginTop: "-4px" }}>
        Specs already promoted to production, listed here regardless of whether any flagged
        samples remain -- review, test against a new upload, or regenerate the same way as a
        candidate above.
      </p>
      {productionSpecs.length > 0 ? (
        productionSpecs.map((group) => (
          <VendorGroupCard key={`production-${group.vendor_name}`} group={group} onChanged={refresh} onPreview={setPreview} />
        ))
      ) : (
        <div className="text-muted small">No parsers promoted to production yet.</div>
      )}
    </>
  );

  return (
    <div className="card" style={{ minWidth: "260px", maxWidth: "700px", width: "100%" }}>
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <h6 className="card-title mb-0">Build a vendor parser</h6>
          <div className="d-flex align-items-center" style={{ gap: "8px" }}>
            {loaded && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary py-0 px-2"
                style={{ fontSize: "11px" }}
                onClick={() => setExpanded(true)}
              >
                View larger
              </button>
            )}
            <button
              type="button"
              className={`btn btn-sm ${loaded ? "btn-outline-secondary" : "btn-outline-primary"}`}
              onClick={refresh}
              disabled={loading}
            >
              {loading ? "Loading..." : loaded ? "Refresh" : "Load list"}
            </button>
          </div>
        </div>
        <p className="card-text small text-muted">
          Confirm/group flagged samples by vendor, generate a declarative extraction spec with AI,
          review its score, then promote it to production.
        </p>

        {loaded ? (
          <div style={{ maxHeight: "420px", overflowY: "auto" }}>{body}</div>
        ) : (
          error && <div className="alert alert-danger py-1 px-2 small">{error}</div>
        )}
      </div>

      <Modal open={expanded} onClose={() => setExpanded(false)} title="Build a vendor parser" maxWidth="900px">
        <div className="p-3">{body}</div>
      </Modal>

      <PdfPreviewModal
        open={!!preview}
        onClose={() => setPreview(null)}
        title={preview ? preview.title : ""}
        url={preview ? preview.url : null}
      />
    </div>
  );
};

export default VendorParserBuilderPanel;
